import React, { useState, useEffect } from 'react';
import { healthAPI } from '../api';

function HealthDashboard() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchHealth();

    if (autoRefresh) {
      const interval = setInterval(fetchHealth, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchHealth = async () => {
    try {
      const response = await healthAPI.checkVPSHealth();
      setHealth(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch health status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'healthy') return '#10b981'; // green
    if (status === 'down') return '#ef4444'; // red
    return '#f59e0b'; // yellow
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy') return '✓';
    if (status === 'down') return '✗';
    return '⚠';
  };

  if (loading) {
    return <div className="loading">Loading health status...</div>;
  }

  return (
    <div className="component-container">
      <div className="component-header">
        <h2>Application Health Monitor</h2>
        <div className="header-actions">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <button className="btn-primary" onClick={fetchHealth}>
            Refresh Now
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {health && (
        <>
          <div className="health-summary">
            <div className="summary-card">
              <h3>Overall Status</h3>
              <p className="summary-value">
                {health.summary.healthy} / {health.summary.total} Healthy
              </p>
              <p className="summary-percentage">{health.summary.uptime} Uptime</p>
            </div>
          </div>

          <div className="health-grid">
            {health.checks.map((check) => (
              <div
                key={check.domain}
                className="health-card"
                style={{
                  borderLeftColor: getStatusColor(check.status)
                }}
              >
                <div className="health-card-header">
                  <span className="status-icon" style={{ color: getStatusColor(check.status) }}>
                    {getStatusIcon(check.status)}
                  </span>
                  <h4>{check.domain}</h4>
                </div>
                <div className="health-card-body">
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={`badge badge-${check.status}`}>
                      {check.status.toUpperCase()}
                    </span>
                  </p>
                  {check.responseTime && (
                    <p>
                      <strong>Response Time:</strong> {check.responseTime}ms
                    </p>
                  )}
                  {check.code && (
                    <p>
                      <strong>HTTP Code:</strong> {check.code}
                    </p>
                  )}
                  {check.error && (
                    <p className="text-danger">
                      <strong>Error:</strong> {check.error}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="component-footer">
            <p className="text-small">
              Last updated: {new Date().toLocaleString()}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default HealthDashboard;
